import { useState, useEffect } from 'react';
import { X, Trash2, ShoppingBag, Loader2, ShieldCheck, Percent, Tag, ChevronDown, Phone } from 'lucide-react';
import { useCart } from '@/context/CartContext';

const API = process.env.REACT_APP_BACKEND_URL;

function getPrintQuality(width) {
  if (!width || width <= 0) return 'unknown';
  if (width >= 2000) return 'good';
  if (width >= 1000) return 'fair';
  return 'poor';
}

const COUNTRIES = [
  "United Kingdom", "United States",
  "Austria", "Belgium", "Bulgaria", "Croatia", "Cyprus", "Czech Republic",
  "Denmark", "Estonia", "Finland", "France", "Germany", "Greece", "Hungary",
  "Ireland", "Italy", "Latvia", "Lithuania", "Luxembourg", "Malta", "Netherlands",
  "Poland", "Portugal", "Romania", "Slovakia", "Slovenia", "Spain", "Sweden",
  "Afghanistan", "Albania", "Algeria", "Argentina", "Armenia", "Australia", "Azerbaijan",
  "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belize", "Bermuda",
  "Bolivia", "Bosnia", "Brazil", "Brunei", "Cambodia", "Cameroon", "Canada",
  "Chile", "China", "Colombia", "Costa Rica", "Cuba", "Dominican Republic",
  "Ecuador", "Egypt", "El Salvador", "Ethiopia", "Fiji", "Georgia", "Ghana",
  "Guatemala", "Honduras", "Hong Kong", "Iceland", "India", "Indonesia", "Iran",
  "Iraq", "Israel", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya",
  "Kuwait", "Lebanon", "Libya", "Malaysia", "Maldives", "Mexico", "Monaco",
  "Mongolia", "Morocco", "Mozambique", "Myanmar", "Nepal", "New Zealand", "Nicaragua",
  "Nigeria", "North Korea", "Norway", "Oman", "Pakistan", "Palestine", "Panama",
  "Paraguay", "Peru", "Philippines", "Qatar", "Russia", "Rwanda", "Saudi Arabia",
  "Senegal", "Serbia", "Singapore", "South Africa", "South Korea", "Sri Lanka", "Sudan",
  "Switzerland", "Syria", "Taiwan", "Tanzania", "Thailand", "Trinidad and Tobago", "Tunisia",
  "Turkey", "UAE", "Uganda", "Ukraine", "Uruguay", "Uzbekistan", "Venezuela", "Vietnam",
  "Yemen", "Zambia", "Zimbabwe"
];

const EU_COUNTRIES = [
  "Austria", "Belgium", "Bulgaria", "Croatia", "Cyprus", "Czech Republic",
  "Denmark", "Estonia", "Finland", "France", "Germany", "Greece", "Hungary",
  "Ireland", "Italy", "Latvia", "Lithuania", "Luxembourg", "Malta", "Netherlands",
  "Poland", "Portugal", "Romania", "Slovakia", "Slovenia", "Spain", "Sweden",
];

function getShippingCost(country) {
  if (!country) return 0;
  if (country === "United Kingdom") return 0;
  if (country === "United States") return 11.99;
  if (EU_COUNTRIES.includes(country)) return 7.99;
  return 16.99;
}

function getShippingLabel(country) {
  if (!country) return '';
  if (country === "United Kingdom") return 'Free UK Shipping';
  if (country === "United States") return 'US Shipping — £11.99';
  if (EU_COUNTRIES.includes(country)) return 'EU Shipping — £7.99';
  return 'International — £16.99';
}

export default function CartDrawer() {
  const { items, removeItem, updateQuantity, clearCart, total, drawerOpen, setDrawerOpen, checkoutNow, setCheckoutNow } = useCart();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);

  // Shipping address
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [postcode, setPostcode] = useState('');
  const [country, setCountry] = useState('United Kingdom');
  const [phone, setPhone] = useState('');

  // Discount code
  const [discountInput, setDiscountInput] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountError, setDiscountError] = useState('');
  const [validatingCode, setValidatingCode] = useState(false);

  // Auto-show checkout when "Checkout Now" was clicked
  useEffect(() => {
    if (drawerOpen && checkoutNow && items.length > 0) {
      setShowCheckoutForm(true);
      setCheckoutNow(false);
    }
  }, [drawerOpen, checkoutNow, items.length, setCheckoutNow]);

  const codeDiscount = appliedDiscount?.percentage || 0;
  const shippingCost = getShippingCost(country);
  const discountedSubtotal = codeDiscount > 0 ? total * (1 - codeDiscount / 100) : total;
  const grandTotal = discountedSubtotal + shippingCost;

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const validateDiscount = async () => {
    const code = discountInput.trim().toUpperCase();
    if (!code) return;
    setValidatingCode(true);
    setDiscountError('');
    try {
      const res = await fetch(`${API}/api/discount/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, subtotal: total }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || 'Invalid code');
      }
      const data = await res.json();
      setAppliedDiscount(data);
      setDiscountInput('');
    } catch (err) {
      setDiscountError(err.message);
    } finally {
      setValidatingCode(false);
    }
  };

  const uploadBase64 = async (dataUrl) => {
    if (!dataUrl || !dataUrl.startsWith('data:')) return '';
    try {
      const arr = dataUrl.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      const u8 = new Uint8Array(bstr.length);
      for (let i = 0; i < bstr.length; i++) u8[i] = bstr.charCodeAt(i);
      const blob = new Blob([u8], { type: mime });
      const ext = mime.includes('png') ? 'png' : 'jpg';
      const fd = new FormData();
      fd.append('file', blob, `logo.${ext}`);
      const res = await fetch(`${API}/api/upload/logo`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      return data.path;
    } catch (err) {
      console.error('Logo upload failed:', err);
      return '';
    }
  };

  const handleCheckout = async () => {
    if (!email.trim() || !isValidEmail(email.trim())) { setError('Valid email is required'); return; }
    if (!fullName.trim()) { setError('Full name is required'); return; }
    if (!address1.trim()) { setError('Address is required'); return; }
    if (!city.trim()) { setError('Town/City is required'); return; }
    if (!postcode.trim()) { setError('Postcode is required'); return; }
    if (!country) { setError('Country is required'); return; }
    if (items.length === 0) return;

    setProcessing(true);
    setError('');

    try {
      const uploadedItems = [];
      for (const item of items) {
        const viewLogosPayload = [];
        if (item.viewLogos) {
          for (const [viewId, vl] of Object.entries(item.viewLogos)) {
            const images = Array.isArray(vl) ? vl : (vl?.userLogo ? [vl] : []);
            for (const img of images) {
              if (!img?.userLogo) continue;
              const origPath = await uploadBase64(img.originalLogoData);
              const cropPath = await uploadBase64(img.userLogo);
              const finish = item.viewFinishes?.[viewId] || null;
              viewLogosPayload.push({
                view_id: viewId,
                original_logo_path: origPath,
                cropped_logo_path: cropPath,
                original_width: img.originalWidth || 0,
                print_quality: getPrintQuality(img.originalWidth),
                logo_placement: img.logoPlacement || { x: 0, y: 0, width: 100, height: 100 },
                finish: finish ? { label: finish.label, price_modifier: finish.price_modifier || 0 } : null,
              });
            }
          }
        }

        const itemPayload = {
          product_slug: item.product_slug,
          selected_color: item.selectedColor || null,
          selected_options: Object.values(item.selectedOptions || {}).map(o => ({ label: o.label, price_modifier: o.price_modifier || 0 })),
          selected_finish: item.selectedFinish || null,
          active_views: item.activeViews || [],
          view_logos: viewLogosPayload,
          original_logo_path: viewLogosPayload[0]?.original_logo_path || '',
          cropped_logo_path: viewLogosPayload[0]?.cropped_logo_path || '',
          logo_placement: viewLogosPayload[0]?.logo_placement || { x: 0, y: 0, width: 100, height: 100 },
        };

        const itemQty = item.qty || 1;
        for (let q = 0; q < itemQty; q++) {
          uploadedItems.push(itemPayload);
        }
      }

      const payload = {
        items: uploadedItems,
        customer_email: email.trim(),
        customer_name: fullName.trim(),
        origin_url: window.location.origin,
        discount_percent: codeDiscount,
        discount_code: appliedDiscount?.code || null,
        shipping_address: {
          full_name: fullName.trim(),
          address_line1: address1.trim(),
          address_line2: address2.trim(),
          city: city.trim(),
          postcode: postcode.trim(),
          country,
          phone: phone.trim(),
        },
      };

      const res = await fetch(`${API}/api/checkout/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Checkout failed');
      }

      const data = await res.json();
      clearCart();
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.message);
      setProcessing(false);
    }
  };

  if (!drawerOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" onClick={() => { setDrawerOpen(false); setShowCheckoutForm(false); }} data-testid="cart-backdrop" />
      <div className="fixed top-0 right-0 z-50 h-full w-full max-w-md bg-white dark:bg-[#0A0A0A] border-l border-slate-200 dark:border-white/10 shadow-2xl flex flex-col animate-in slide-in-from-right" data-testid="cart-drawer">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-white/10">
          <div className="flex items-center gap-2.5">
            <ShoppingBag className="w-5 h-5 text-[#00E5FF]" />
            <h2 className="font-['Outfit'] text-lg font-bold text-[#0A0A0A] dark:text-white">{showCheckoutForm ? 'Checkout' : 'Basket'}</h2>
            {!showCheckoutForm && <span className="text-xs font-medium text-slate-400 bg-slate-100 dark:bg-white/5 px-2 py-0.5 rounded-full">{items.length}</span>}
          </div>
          <button onClick={() => { setDrawerOpen(false); setShowCheckoutForm(false); }} data-testid="close-cart-btn" className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-violet-400/10 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Items */}
          <div className="p-5 space-y-3">
            {items.length === 0 ? (
              <div className="text-center py-16">
                <ShoppingBag className="w-10 h-10 text-slate-200 dark:text-slate-700 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Your basket is empty</p>
                <p className="text-xs text-slate-400 mt-1">Customise a product and add it here</p>
              </div>
            ) : (
              items.map(item => (
                <div key={item.id} className="flex gap-3 p-3 bg-slate-50 dark:bg-white/[0.02] border border-slate-200 dark:border-white/5 rounded-xl" data-testid={`cart-item-${item.id}`}>
                  {item.viewLogos && (() => {
                    const allImgs = Object.values(item.viewLogos).flat();
                    const first = allImgs.find(vl => vl?.userLogo);
                    return first ? (
                      <div className="w-14 h-14 rounded-lg border border-slate-200 dark:border-white/10 overflow-hidden bg-white dark:bg-[#111] flex-shrink-0">
                        <img src={first.userLogo} alt="Design" className="w-full h-full object-contain" />
                      </div>
                    ) : null;
                  })() || (
                    <div className="w-14 h-14 rounded-lg bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 flex items-center justify-center flex-shrink-0">
                      <ShoppingBag className="w-5 h-5 text-slate-300 dark:text-slate-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#0A0A0A] dark:text-white truncate">{item.product_title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {item.selectedColor && (
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded-full border border-slate-200 dark:border-white/20" style={{ backgroundColor: item.selectedColor.hex_code }} />
                          <span className="text-[10px] text-slate-400">{item.selectedColor.name}</span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm font-bold text-[#0A0A0A] dark:text-white mt-1">£{((item.totalPrice || 0) * (item.qty || 1)).toFixed(2)}{(item.qty || 1) > 1 && <span className="text-[10px] text-slate-400 font-normal ml-1">({item.qty} x £{item.totalPrice?.toFixed(2)})</span>}</p>
                    <div className="flex items-center gap-1 mt-1.5">
                      <button onClick={() => { if ((item.qty || 1) <= 1) removeItem(item.id); else updateQuantity(item.id, (item.qty || 1) - 1); }} data-testid={`qty-down-${item.id}`}
                        className="w-6 h-6 rounded-md flex items-center justify-center text-xs font-medium border border-slate-200 dark:border-white/10 text-slate-500 hover:text-[#0A0A0A] dark:hover:text-white hover:bg-slate-100 dark:hover:bg-violet-400/5 transition-colors">-</button>
                      <span className="w-6 h-6 flex items-center justify-center text-xs font-semibold text-[#0A0A0A] dark:text-white" data-testid={`qty-val-${item.id}`}>{item.qty || 1}</span>
                      <button onClick={() => updateQuantity(item.id, (item.qty || 1) + 1)} data-testid={`qty-up-${item.id}`}
                        className="w-6 h-6 rounded-md flex items-center justify-center text-xs font-medium border border-slate-200 dark:border-white/10 text-slate-500 hover:text-[#0A0A0A] dark:hover:text-white hover:bg-slate-100 dark:hover:bg-violet-400/5 transition-colors">+</button>
                    </div>
                  </div>
                  <button onClick={() => removeItem(item.id)} data-testid={`remove-item-${item.id}`}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-300 hover:text-rose-400 hover:bg-rose-500/10 transition-colors flex-shrink-0 self-center">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Checkout Form */}
          {showCheckoutForm && items.length > 0 && (
            <div className="px-5 pb-5 space-y-4">
              {/* Contact Details */}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-500 block mb-2">Contact Details</label>
                <div className="space-y-2">
                  <input data-testid="checkout-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email *"
                    className="w-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-sm text-[#0A0A0A] dark:text-white placeholder:text-slate-400 focus:outline-none focus:border-[#00E5FF]/50" />
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <input data-testid="checkout-phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                      placeholder="Phone (optional)"
                      className="w-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl pl-9 pr-4 py-3 text-sm text-[#0A0A0A] dark:text-white placeholder:text-slate-400 focus:outline-none focus:border-[#00E5FF]/50" />
                  </div>
                  <p className="text-[10px] text-slate-400 dark:text-slate-600 pl-1">Add your number for real-time delivery updates, otherwise tracking is via email</p>
                </div>
              </div>

              {/* Shipping Address */}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-500 block mb-2">Shipping Address</label>
                <div className="space-y-2">
                  <input data-testid="checkout-fullname" type="text" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Full Name *"
                    className="w-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-sm text-[#0A0A0A] dark:text-white placeholder:text-slate-400 focus:outline-none focus:border-[#00E5FF]/50" />
                  <input data-testid="checkout-address1" type="text" value={address1} onChange={e => setAddress1(e.target.value)} placeholder="Address Line 1 *"
                    className="w-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-sm text-[#0A0A0A] dark:text-white placeholder:text-slate-400 focus:outline-none focus:border-[#00E5FF]/50" />
                  <input data-testid="checkout-address2" type="text" value={address2} onChange={e => setAddress2(e.target.value)} placeholder="Address Line 2 (optional)"
                    className="w-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-sm text-[#0A0A0A] dark:text-white placeholder:text-slate-400 focus:outline-none focus:border-[#00E5FF]/50" />
                  <div className="grid grid-cols-2 gap-2">
                    <input data-testid="checkout-city" type="text" value={city} onChange={e => setCity(e.target.value)} placeholder="Town / City *"
                      className="w-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-sm text-[#0A0A0A] dark:text-white placeholder:text-slate-400 focus:outline-none focus:border-[#00E5FF]/50" />
                    <input data-testid="checkout-postcode" type="text" value={postcode} onChange={e => setPostcode(e.target.value)} placeholder="Postcode *"
                      className="w-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-sm text-[#0A0A0A] dark:text-white placeholder:text-slate-400 focus:outline-none focus:border-[#00E5FF]/50" />
                  </div>
                  <div className="relative">
                    <select data-testid="checkout-country" value={country} onChange={e => setCountry(e.target.value)}
                      className="w-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-sm text-[#0A0A0A] dark:text-white focus:outline-none focus:border-[#00E5FF]/50 appearance-none cursor-pointer">
                      {COUNTRIES.map(c => <option key={c} value={c} className="text-[#0A0A0A] bg-white dark:bg-[#1a1a1a]">{c}</option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                  {country && (
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${shippingCost === 0 ? 'bg-green-500/10 border border-green-500/20 text-green-500' : 'bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-400'}`} data-testid="shipping-cost-label">
                      {getShippingLabel(country)}
                    </div>
                  )}
                </div>
              </div>

              {/* Discount Code */}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-500 block mb-2">Discount Code</label>
                {appliedDiscount ? (
                  <div className="flex items-center justify-between bg-green-500/10 border border-green-500/20 rounded-xl px-4 py-3" data-testid="applied-discount">
                    <div className="flex items-center gap-2">
                      <Tag className="w-3.5 h-3.5 text-green-500" />
                      <span className="text-sm font-mono font-bold text-green-500">{appliedDiscount.code}</span>
                      <span className="text-xs text-green-400">— {appliedDiscount.percentage}% off</span>
                    </div>
                    <button onClick={() => setAppliedDiscount(null)} className="text-xs text-slate-400 hover:text-rose-400 transition-colors">Remove</button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input data-testid="discount-code-checkout-input" type="text" value={discountInput} onChange={e => setDiscountInput(e.target.value.toUpperCase())}
                      placeholder="Enter code" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), validateDiscount())}
                      className="flex-1 bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-sm text-[#0A0A0A] dark:text-white placeholder:text-slate-400 focus:outline-none focus:border-[#00E5FF]/50 font-mono uppercase tracking-wider" />
                    <button onClick={validateDiscount} disabled={validatingCode || !discountInput.trim()} data-testid="apply-discount-btn"
                      className="bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-[#0A0A0A] dark:text-white px-4 rounded-xl text-sm font-medium hover:border-[#00E5FF]/30 transition-colors disabled:opacity-50">
                      {validatingCode ? '...' : 'Apply'}
                    </button>
                  </div>
                )}
                {discountError && <p className="text-xs text-rose-400 mt-1.5">{discountError}</p>}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-slate-200 dark:border-white/10 p-5 space-y-3">
            {showCheckoutForm ? (
              <>
                {/* Price Summary */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Subtotal</span>
                    <span className="text-[#0A0A0A] dark:text-white">£{total.toFixed(2)}</span>
                  </div>
                  {codeDiscount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-green-500 flex items-center gap-1"><Percent className="w-3 h-3" /> {codeDiscount}% off (Code: {appliedDiscount.code})</span>
                      <span className="text-green-500">-£{(total * codeDiscount / 100).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Shipping ({country})</span>
                    <span className={shippingCost === 0 ? 'text-green-500 font-medium' : 'text-[#0A0A0A] dark:text-white'}>{shippingCost === 0 ? 'FREE' : `£${shippingCost.toFixed(2)}`}</span>
                  </div>
                  <div className="border-t border-slate-200 dark:border-white/10 pt-2 flex justify-between">
                    <span className="text-sm font-semibold text-[#0A0A0A] dark:text-white">Total</span>
                    <span className="font-['Outfit'] text-xl font-bold text-[#0A0A0A] dark:text-white" data-testid="checkout-grand-total">£{grandTotal.toFixed(2)}</span>
                  </div>
                </div>

                {error && <p data-testid="cart-checkout-error" className="text-xs text-rose-400 bg-rose-500/10 rounded-lg px-3 py-2">{error}</p>}

                <button onClick={handleCheckout} disabled={processing || !email.trim()} data-testid="cart-pay-btn"
                  className="w-full bg-[#0A0A0A] dark:bg-[#8B5CF6] text-white py-4 rounded-xl font-semibold text-sm hover:bg-[#00E5FF] hover:text-[#0A0A0A] dark:hover:bg-violet-400 transition-all disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-2">
                  {processing ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</> : <>Pay £{grandTotal.toFixed(2)}</>}
                </button>

                <button onClick={() => setShowCheckoutForm(false)} className="w-full text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors py-1">
                  Back to basket
                </button>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Subtotal ({items.length} item{items.length !== 1 ? 's' : ''})</span>
                  <span className="font-['Outfit'] text-xl font-bold text-[#0A0A0A] dark:text-white" data-testid="cart-total">£{total.toFixed(2)}</span>
                </div>
                <button onClick={() => setShowCheckoutForm(true)} data-testid="cart-checkout-btn"
                  className="w-full bg-[#0A0A0A] dark:bg-[#8B5CF6] text-white py-4 rounded-xl font-semibold text-sm hover:bg-[#00E5FF] hover:text-[#0A0A0A] dark:hover:bg-violet-400 transition-all">
                  Checkout
                </button>
                <button onClick={clearCart} data-testid="clear-cart-btn" className="w-full text-xs text-slate-400 hover:text-rose-400 transition-colors py-1">
                  Clear basket
                </button>
              </>
            )}

            <div className="flex items-center justify-center gap-2 text-[10px] text-slate-400">
              <ShieldCheck className="w-3 h-3" />
              <span>Secured by Stripe</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
